import axios from 'axios';
import { ENV } from '@/config/environment';
import { getSession } from '@/core/auth/supabase-auth';

/**
 * KeepAliveService - Prevents Cloud Run instance termination during background processing
 *
 * Problem: When browser tab is minimized/unfocused, browsers throttle timers and
 * no HTTP requests are sent. Cloud Run sees this as inactivity and kills the instance.
 *
 * Solution: Send HTTP keepalive requests every 30 seconds when tab is hidden
 * to maintain Cloud Run instance activity.
 */
class KeepAliveService {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private sessionId: string | null = null;
  private readonly KEEPALIVE_INTERVAL = 30000; // 30 seconds
  private isListening = false;

  /**
   * Start keepalive monitoring for a session
   * @param sessionId - The active session ID to keep alive
   */
  start(sessionId: string): void {
    // Prevent duplicate listeners
    if (this.isListening) {
      this.stop();
    }

    this.sessionId = sessionId;
    this.isListening = true;

    // Add visibility change listener
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // If tab is already hidden, start interval immediately
    if (document.hidden) {
      this.startInterval();
    }
  }

  /**
   * Stop keepalive monitoring and cleanup
   */
  stop(): void {
    this.stopInterval();

    if (this.isListening) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.isListening = false;
    }

    this.sessionId = null;
  }

  /**
   * Handle visibility change events
   */
  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      // Tab hidden - start keepalive pings
      this.startInterval();
    } else {
      // Tab visible - normal polling is sufficient
      this.stopInterval();
    }
  };

  /**
   * Start the keepalive interval
   */
  private startInterval(): void {
    if (this.intervalId) {
      return; // Already running
    }

    // Send first keepalive immediately
    this.sendKeepalive();

    // Then continue at interval
    this.intervalId = setInterval(() => {
      this.sendKeepalive();
    }, this.KEEPALIVE_INTERVAL);
  }

  /**
   * Stop the keepalive interval
   */
  private stopInterval(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * Send keepalive request to backend
   */
  private async sendKeepalive(): Promise<void> {
    if (!this.sessionId) {
      return;
    }

    try {
      // Get auth token for the request
      const session = await getSession();
      const token = session?.access_token;

      if (!token) {
        console.warn('[KeepAlive] No auth token available, skipping ping');
        return;
      }

      await axios.get(`${ENV.API_BASE_URL}/api/keepalive/${this.sessionId}`, {
        timeout: 10000, // 10 second timeout
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      // Log but don't throw - keepalive failures shouldn't crash the app
      console.warn('[KeepAlive] Ping failed:', error);
    }
  }

  /**
   * Check if keepalive is currently active
   */
  isActive(): boolean {
    return this.isListening;
  }
}

// Export singleton instance
export const keepAliveService = new KeepAliveService();
